/*
 * Forge + Finish hero slider
 *
 * Progressive enhancement: the first slide is rendered active by Liquid, so the
 * banner is fully visible and readable before this script runs.
 */

const FF_HERO_SWIPE_THRESHOLD = 45;
const FF_HERO_MIN_SPEED = 1500;

class FFHeroSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-ff-track]');
    this.slides = Array.from(this.querySelectorAll('.ff-hero__slide'));
    if (this.slides.length === 0) return;

    this.dots = Array.from(this.querySelectorAll('[data-ff-dot]'));
    this.prevButton = this.querySelector('[data-ff-prev]');
    this.nextButton = this.querySelector('[data-ff-next]');

    this.index = 0;
    this.timer = null;
    this.pointerStartX = null;

    this.effect = this.dataset.transition === 'fade' ? 'fade' : 'slide';
    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.pauseOnHover = this.dataset.pauseOnHover === 'true';

    const speed = parseInt(this.dataset.autoplaySpeed, 10);
    this.autoplaySpeed = Number.isFinite(speed) ? Math.max(FF_HERO_MIN_SPEED, speed) : 5000;
    this.autoplayRequested = this.dataset.autoplay === 'true' && this.slides.length > 1;

    this.style.setProperty('--ff-hero-autoplay', `${this.autoplaySpeed}ms`);

    this.bindEvents();
    this.goTo(0, { restartAutoplay: false });
    this.startAutoplay();
  }

  disconnectedCallback() {
    this.stopAutoplay();
    this.listeners.forEach(([target, type, handler]) => target.removeEventListener(type, handler));
    this.listeners = [];
  }

  get autoplayAllowed() {
    return this.autoplayRequested && !this.motionQuery.matches;
  }

  on(target, type, handler, options) {
    target.addEventListener(type, handler, options);
    this.listeners.push([target, type, handler]);
  }

  bindEvents() {
    this.listeners = [];

    if (this.prevButton) this.on(this.prevButton, 'click', () => this.goTo(this.index - 1));
    if (this.nextButton) this.on(this.nextButton, 'click', () => this.goTo(this.index + 1));

    this.dots.forEach((dot) => {
      this.on(dot, 'click', () => this.goTo(Number(dot.dataset.ffDot)));
    });

    this.on(this, 'keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.goTo(this.index - 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.goTo(this.index + 1);
      }
    });

    if (this.pauseOnHover) {
      this.on(this, 'mouseenter', () => this.pause());
      this.on(this, 'mouseleave', () => this.resume());
      this.on(this, 'focusin', () => this.pause());
      this.on(this, 'focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.resume();
      });
    }

    // A slider ticking away in a hidden tab wastes work and lands the visitor on
    // an arbitrary slide when they return.
    this.on(document, 'visibilitychange', () => {
      if (document.hidden) {
        this.pause();
      } else {
        this.resume();
      }
    });

    this.on(this, 'pointerdown', (event) => {
      if (event.pointerType === 'mouse') return;
      this.pointerStartX = event.clientX;
    });

    this.on(this, 'pointerup', (event) => {
      if (this.pointerStartX === null) return;
      const delta = event.clientX - this.pointerStartX;
      this.pointerStartX = null;
      if (Math.abs(delta) < FF_HERO_SWIPE_THRESHOLD) return;
      this.goTo(delta < 0 ? this.index + 1 : this.index - 1);
    });

    this.on(this, 'pointercancel', () => {
      this.pointerStartX = null;
    });

    // Selecting a slide in the theme editor should reveal that slide, not fight
    // the merchant by rotating away from it.
    this.on(this, 'shopify:block:select', (event) => {
      const slide = event.target.closest('.ff-hero__slide');
      if (!slide) return;
      this.goTo(Number(slide.dataset.index), { restartAutoplay: false });
      this.stopAutoplay();
    });

    this.on(this, 'shopify:block:deselect', () => this.startAutoplay());
  }

  goTo(target, { restartAutoplay = true } = {}) {
    const total = this.slides.length;
    this.index = ((target % total) + total) % total;

    if (this.effect === 'slide' && this.track) {
      this.track.style.transform = `translate3d(-${this.index * 100}%, 0, 0)`;
    }

    this.slides.forEach((slide, i) => {
      const isActive = i === this.index;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      // Keeps off-screen links and buttons out of the tab order.
      slide.inert = !isActive;
    });

    this.dots.forEach((dot, i) => {
      const isActive = i === this.index;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    if (restartAutoplay) this.startAutoplay();
  }

  startAutoplay() {
    this.stopAutoplay();
    if (!this.autoplayAllowed) return;
    this.classList.add('is-autoplaying');
    this.classList.remove('is-paused');
    this.timer = window.setInterval(() => this.goTo(this.index + 1, { restartAutoplay: false }), this.autoplaySpeed);
  }

  stopAutoplay() {
    if (this.timer) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
    this.classList.remove('is-autoplaying');
  }

  pause() {
    if (!this.timer) return;
    window.clearInterval(this.timer);
    this.timer = null;
    this.classList.add('is-paused');
  }

  resume() {
    if (this.timer || !this.autoplayAllowed) return;
    if (this.pauseOnHover && this.matches(':hover')) return;
    this.classList.remove('is-paused');
    this.timer = window.setInterval(() => this.goTo(this.index + 1, { restartAutoplay: false }), this.autoplaySpeed);
  }
}

if (!customElements.get('ff-hero-slider')) {
  customElements.define('ff-hero-slider', FFHeroSlider);
}
